import React from 'react';
import { 
  Mail, 
  Phone, 
  MapPin,
  ChevronUp,
  Globe,
  Users,
  Newspaper,
  ArrowRight
} from 'lucide-react';
import logo from '../../images/logo-xibaru.png';

const ScrollToTopButton = ({ className = "" }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-4 right-4 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-r from-red-600 to-blue-800 hover:from-red-700 hover:to-blue-900 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group ${className}`}
      aria-label="Retour en haut"
    >
      <ChevronUp className="h-5 w-5 sm:h-6 sm:w-6 text-white group-hover:-translate-y-1 transition-transform duration-300" />
    </button>
  );
};

const Footer = () => {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter subscription');
  };

  return (
    <>
      <ScrollToTopButton />
      
      <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-blue-800/5 to-red-600/5"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(239,68,68,0.1),transparent_50%)]"></div>
        </div>

        <div className="container mx-auto px-4 py-8 sm:py-12 lg:py-16 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Section Logo et À propos */}
            <div className="sm:col-span-2 lg:col-span-1 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-center sm:justify-start">
                  <img 
                    src={logo}
                    alt="Logo Xibaru Bambouck"
                    className="w-20 h-16 sm:w-24 sm:h-20 object-contain"
                  />
                </div>
                
                <div className="space-y-3 text-center sm:text-left">
                  <h4 className="text-white font-semibold text-sm flex items-center justify-center sm:justify-start space-x-2">
                    <Users className="w-4 h-4" />
                    <span>À propos</span>
                  </h4>
                  <p className="text-slate-300 text-sm leading-relaxed max-w-sm mx-auto sm:mx-0">
                    Xibaru Bambouck, votre source d'information locale de référence pour la région du Bambouck. 
                    Nous couvrons l'actualité politique, sociale, sportive et culturelle avec engagement et proximité.
                  </p>
                  <a 
                    href="/about" 
                    className="group inline-flex items-center space-x-2 text-red-400 hover:text-red-300 transition-all duration-300 text-sm"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">En savoir plus</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-all duration-300" />
                  </a>
                </div>
              </div>
              
              {/* Réseaux sociaux */}
              <div className="space-y-4 text-center sm:text-left">
                <h4 className="text-white font-semibold text-sm">Suivez-nous</h4>
                <div className="flex space-x-3 justify-center sm:justify-start">
                  <a 
                    href="https://www.facebook.com/xibarubambouck" 
                    className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center hover:shadow-lg transition-all duration-300 hover:scale-110 group"
                    aria-label="Facebook"
                  >
                    <span className="text-white text-sm font-bold group-hover:scale-110 transition-transform">f</span>
                  </a>
                  <a 
                    href="https://api.whatsapp.com/send?phone=221774369394&text&context=AffoSsEX08EdK4TvEUB_dFo3fxt0V8tZAjxL81kqh00KnFdqOnaIRImX5cTDegr2g9XrVsCnHckRwbk0esv0NbUN_wEmFGcpFbq0leVV9clltGjIyKjMKgD4rv3DdsZecmv3lTWLyYEYp8xvhjCNKFsGqg&source&app=facebook" 
                    className="w-10 h-10 bg-gradient-to-r from-green-600 to-green-700 rounded-full flex items-center justify-center hover:shadow-lg transition-all duration-300 hover:scale-110 group"
                    aria-label="WhatsApp"
                  >
                    <span className="text-white text-sm font-bold group-hover:scale-110 transition-transform">W</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@user-ij6tn9lu5b" 
                    className="w-10 h-10 bg-gradient-to-r from-red-600 to-red-700 rounded-full flex items-center justify-center hover:shadow-lg transition-all duration-300 hover:scale-110 group"
                    aria-label="YouTube"
                  >
                    <span className="text-white text-sm font-bold group-hover:scale-110 transition-transform">YT</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Section Navigation */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-white text-center sm:text-left">Navigation</h3>
              <ul className="space-y-3">
                {[
                  { to: '/', label: 'Accueil' },
                  { to: '/category/politique', label: 'Politique' },
                  { to: '/category/societe', label: 'Société' },
                  { to: '/category/sport', label: 'Sport' },
                  { to: '/category/religion', label: 'Religion' },
                  { to: '/category/international', label: 'International' },
                  { to: '/category/necrologie', label: 'Nécrologie' }
                ].map((item) => (
                  <li key={item.to} className="text-center sm:text-left">
                    <a 
                      href="#" 
                      className="group inline-flex items-center space-x-2 text-slate-300 hover:text-white transition-all duration-300 text-sm"
                    >
                      <ArrowRight className="w-3 h-3 text-slate-500 group-hover:text-red-400 group-hover:translate-x-1 transition-all duration-300" />
                      <span className="group-hover:translate-x-1 transition-transform duration-300">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Section Contact */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-white text-center sm:text-left">Contact</h3>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="p-2 bg-red-600/20 rounded-lg">
                    <MapPin className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-white text-sm font-medium">Koungheul ville</p>
                    <p className="text-slate-300 text-xs">Immeuble Mor Sine en face terrain Municipal</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Phone className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-white text-sm font-medium">+221 77 436 93 94</p>
                    <p className="text-white text-sm font-medium">+221 77 144 79 30</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                  <div className="p-2 bg-red-600/20 rounded-lg">
                    <Mail className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-white text-sm font-medium break-all">amsarr82@gmail.com</p>
                    <p className="text-white text-sm font-medium break-all">contact@xibarubambouck.net</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Newsletter */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-white text-center sm:text-left">Newsletter</h3>
              
              <div className="space-y-4">
                <p className="text-slate-300 text-sm text-center sm:text-left">
                  Recevez nos dernières actualités directement dans votre boîte mail.
                </p>
                
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <div className="relative">
                    <input
                      type="email"
                      placeholder="Votre email"
                      className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 text-sm"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-blue-800 hover:from-red-700 hover:to-blue-900 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg transform hover:scale-105 flex items-center justify-center space-x-2 text-sm"
                  >
                    <span>S'abonner</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Séparateur */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-700/50 to-transparent h-px"></div>
        </div>

        {/* Copyright */}
        <div className="bg-slate-900/70 backdrop-blur-sm border-t border-slate-700/30 relative z-10">
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <div className="flex flex-col items-center space-y-3 sm:space-y-2">
              <p className="text-slate-400 text-xs sm:text-sm text-center">
                © 2025 <span className="font-semibold text-white">Xibaru Bambouck</span>. Tous droits réservés.
              </p>
              <div className="text-xs text-slate-500 text-center">
                <span>Fait avec ❤️ pour le Bambouck</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export { ScrollToTopButton };
export default Footer;